import { useEffect } from 'react';

import { useDispatch } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import type { BehandlingsvalgDto, SamvalgsverktoyDto } from '../types/cms';

import { mapCultureNameToLanguageLocale } from '@helsenorge/core-cms/shared/language';
import type { LanguageModel } from '@helsenorge/core-cms/types/entities/cms.v1entities';
import { LanguageLocales } from '@helsenorge/core-utils/constants/languages';
import { getUserLanguage } from '@helsenorge/framework-utils/hn-language';
import { getPath } from '@helsenorge/framework-utils/hn-page';
import { allowedLanguagesReplaced, languageChanged, selectCurrentLanguage, selectLanguages } from '@helsenorge/framework-utils/language';
import { HNeventSetDriftsmeldingPath, HNeventSetNoMarginFooter } from '@helsenorge/framework-utils/web-component/events';

import { mapLanguageLocaleToCultureName } from './language';
import { getRelativePath } from './url-utils';
import { useGetBehandlingsvalgQuery } from '../store/behandlingsvalgApi';
import { useAppSelector } from '../store/hooks';
import { breadcrumbChanged } from '../store/reducers/breadcrumb';
import { useGetSamvalgsverktoyQuery } from '../store/samvalgsverktoyApi';

export interface SamvalgPageContent {
  verktoyPage: SamvalgsverktoyDto | undefined;
  isVerktoyPageError: boolean;
  isVerktoyPageLoading: boolean;
  behandlingsvalgPage: BehandlingsvalgDto | undefined;
  isBehandlingsvalgError: boolean;
  isBehandlingsvalgPageLoading: boolean;
}

export const useSamvalgPageContent = (): SamvalgPageContent => {
  const { verktoynavn: verktoyNavn, behandlingsvalgnavn: behandlingsvalgNavn } = useParams<{
    verktoynavn: string;
    behandlingsvalgnavn: string;
  }>();
  const [searchParams] = useSearchParams();
  const languageCode = searchParams.get('languageCode');
  const currentLanguage = useAppSelector(state => selectCurrentLanguage(state.language));

  const mappedLanguageCode = mapCultureNameToLanguageLocale(languageCode) ?? currentLanguage;
  const languages = useAppSelector(state => selectLanguages(state.language));
  const userLanguage = getUserLanguage();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    data: verktoyPage,
    isError: isVerktoyPageError,
    isLoading: isVerktoyPageLoading,
  } = useGetSamvalgsverktoyQuery({
    verktoyNavn: verktoyNavn ?? '',
    language: mapCultureNameToLanguageLocale(languageCode) ?? LanguageLocales.NORWEGIAN,
  });
  const {
    data: behandlingsvalgPage,
    isError: isBehandlingsvalgError,
    isLoading: isBehandlingsvalgPageLoading,
  } = useGetBehandlingsvalgQuery(
    {
      behandlingsvalgNavn: behandlingsvalgNavn ?? '',
      language: mapCultureNameToLanguageLocale(languageCode) ?? LanguageLocales.NORWEGIAN,
    },
    { skip: !behandlingsvalgNavn }
  );

  const page = behandlingsvalgPage || verktoyPage;

  const navigateToResolvedUrl = (languageVersions: LanguageModel[] | undefined, language: LanguageLocales): void => {
    const mappedLanguage = mapLanguageLocaleToCultureName(language);
    const matchingVersion = languageVersions?.find(lang => mappedLanguage === lang.cultureName);
    if (matchingVersion?.resolvedUrl) {
      const basePath = getRelativePath(matchingVersion.resolvedUrl);
      const url = new URL(matchingVersion.resolvedUrl);
      const searchParams = new URLSearchParams(url.search);
      const pathParts = basePath
        .split('/')
        .filter(part => part)
        .map(part => part.split('?'))[0];

      const newPath =
        behandlingsvalgNavn && !pathParts.includes(behandlingsvalgNavn) ? `/${pathParts[0]}/${behandlingsvalgNavn}` : `/${pathParts[0]}`;
      if (searchParams.toString()) {
        navigate(`${newPath}?${searchParams.toString()}`, { replace: true });
      } else {
        navigate(newPath, { replace: true });
      }
    }
  };

  useEffect(() => {
    if (!page?.languageVersions) {
      return;
    }

    const availableLanguages = page.languageVersions
      .map(lang => mapCultureNameToLanguageLocale(lang.cultureName))
      .filter(Boolean) as LanguageLocales[];

    dispatch(allowedLanguagesReplaced(availableLanguages));
    if (availableLanguages.includes(mappedLanguageCode)) {
      dispatch(languageChanged(mappedLanguageCode));
    }
  }, [page?.languageVersions, mappedLanguageCode, dispatch]);

  useEffect(() => {
    if (!page?.languageVersions || !languages.includes(currentLanguage) || !languages.includes(mappedLanguageCode)) {
      return;
    }

    if (userLanguage && userLanguage !== currentLanguage && userLanguage === mappedLanguageCode) {
      dispatch(languageChanged(userLanguage));
      navigateToResolvedUrl(page.languageVersions, userLanguage);
    } else {
      navigateToResolvedUrl(page.languageVersions, currentLanguage);
    }
  }, [currentLanguage, page?.languageVersions, mappedLanguageCode, userLanguage]);

  useEffect(() => {
    HNeventSetNoMarginFooter();
  }, []);

  useEffect(() => {
    const id = setTimeout((): void => {
      HNeventSetDriftsmeldingPath(`${getPath()}_${verktoyNavn}`);
    }, 500);

    return (): void => {
      clearTimeout(id);
    };
  }, [verktoyNavn]);

  useEffect(() => {
    if (!page) {
      return;
    }
    dispatch(breadcrumbChanged(page.breadCrumb));
  }, [page?.breadCrumb]);

  return {
    verktoyPage,
    isVerktoyPageError,
    isVerktoyPageLoading,
    behandlingsvalgPage,
    isBehandlingsvalgError,
    isBehandlingsvalgPageLoading,
  };
};
